import { action, register, types } from "@gadgetinc/mobx-quick-tree";
import { contentHash, GadgetDomainModelClass } from "./utils";

export const BrandingColors = ["zinc", "rose", "red", "amber", "lime", "emerald", "cyan", "blue", "fuchsia"] as const;
export type BrandingColor = (typeof BrandingColors)[number];

export const cornerStyles = ["soft", "sharp"] as const;
export type CornerStyle = (typeof cornerStyles)[number];

export const appearanceOptions = ["light", "dark"] as const;
export type Appearance = (typeof appearanceOptions)[number];

export const brandColorNameToHex = (colorName: string, isLight: boolean) => {
  switch (colorName) {
    case "zinc":
      return isLight ? "#18181b" : "#fafafa";
    case "rose":
      return "#f43f5e";
    case "red":
      return "#ef4444";
    case "amber":
      return "#f59e0b";
    case "lime":
      return "#84cc16";
    case "emerald":
      return "#10b981";
    case "cyan":
      return "#06b6d4";
    case "blue":
      return "#3b82f6";
    case "fuchsia":
      return "#d946ef";

    default:
      throw new Error(`Unknown color name: ${colorName}`);
  }
};

@register
export class FrontendBranding extends GadgetDomainModelClass("FrontendBranding", {
  appearance: types.optional(types.enumeration(appearanceOptions), "light"),
  primaryColor: types.optional(types.enumeration(BrandingColors), "zinc"),
  accentColor: types.optional(types.enumeration(BrandingColors), "zinc"),
  fontFamily: types.optional(types.string, "font-sans"),
  cornerStyle: types.optional(types.enumeration(cornerStyles), "soft"),
  autoLogo: types.maybeNull(types.string),
}) {
  get contentHash() {
    return contentHash([this.appearance, this.primaryColor, this.accentColor, this.fontFamily, this.cornerStyle, this.autoLogo]);
  }

  get isLight() {
    return this.appearance === "light";
  }

  get primaryColorHex() {
    return brandColorNameToHex(this.primaryColor, this.isLight);
  }

  get accentColorHex() {
    return brandColorNameToHex(this.accentColor, this.isLight);
  }

  dataBlob() {
    return {
      appearance: this.appearance,
      primaryColor: this.primaryColor,
      accentColor: this.accentColor,
      fontFamily: this.fontFamily,
      cornerStyle: this.cornerStyle,
    };
  }

  @action
  setAppearance(appearance: Appearance) {
    this.appearance = appearance;
  }

  @action
  setPrimaryColor(primaryColor: BrandingColor) {
    this.primaryColor = primaryColor;
  }

  @action
  setAccentColor(accentColor: BrandingColor) {
    this.accentColor = accentColor;
  }

  @action
  setFontFamily(fontFamily: string) {
    this.fontFamily = fontFamily;
  }

  @action
  setCornerStyle(cornerStyle: CornerStyle) {
    this.cornerStyle = cornerStyle;
  }

  @action
  setAutoLogo(autoLogo: string | null) {
    this.autoLogo = autoLogo;
  }
}
